import React, { createContext, useContext, useMemo, useReducer } from 'react';

const CartContext = createContext(null);

const emptyCheckout = () => ({
  cliente_nombre: '',
  cliente_telefono: '',
  cliente_documento: '',
  cliente_ciudad: '',
  cliente_direccion: '',
  cliente_email: '',
  notas: '',
  transportadora: '',
  transportadora_otra: '',
  horario_recibo: '',
  tipo_documento: 'remision',
  forma_pago: 'contado',
});

function displayNombre(product) {
  const n = product?.nombre || '';
  if (!n || n.startsWith('Producto ')) return '';
  return n;
}

function newLineId() {
  return `L${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
}

function cartReducer(state, action) {
  switch (action.type) {
    case 'ADD': {
      const { product } = action;
      const cantidad = Math.max(1, Number(action.cantidad) || 1);
      const observaciones = String(action.observaciones || '').trim();
      const opcionId = action.opcion_id || null;
      const opcionNombre = String(action.opcion_nombre || '').trim() || null;
      const existing = state.items.find(
        (i) =>
          i.codigo === product.codigo &&
          (i.opcion_id || null) === opcionId &&
          (i.observaciones || '') === observaciones
      );
      if (existing) {
        return {
          ...state,
          items: state.items.map((i) =>
            i.lineId === existing.lineId ? { ...i, cantidad: i.cantidad + cantidad } : i
          ),
        };
      }
      const baseName = displayNombre(product) || `Producto ${product.codigo}`;
      return {
        ...state,
        items: [
          ...state.items,
          {
            lineId: newLineId(),
            producto_id: product.id || product.producto_id || null,
            codigo: product.codigo,
            nombre: opcionNombre ? `${baseName} · ${opcionNombre}` : baseName,
            precio: Number(product.precio) || 0,
            imagen: action.imagen || product.imagen,
            cantidad,
            observaciones,
            opcion_id: opcionId,
            opcion_nombre: opcionNombre,
          },
        ],
      };
    }
    case 'SET_QTY': {
      const qty = Math.max(0, action.qty);
      const key = action.lineId || action.codigo;
      if (qty === 0) {
        return {
          ...state,
          items: state.items.filter((i) => (action.lineId ? i.lineId !== key : i.codigo !== key)),
        };
      }
      return {
        ...state,
        items: state.items.map((i) => {
          const match = action.lineId ? i.lineId === key : i.codigo === key;
          return match ? { ...i, cantidad: qty } : i;
        }),
      };
    }
    case 'REMOVE':
      return {
        ...state,
        items: state.items.filter((i) =>
          action.lineId ? i.lineId !== action.lineId : i.codigo !== action.codigo
        ),
      };
    case 'REMOVE_BY_CODIGO':
      return {
        ...state,
        items: state.items.filter((i) => String(i.codigo) !== String(action.codigo)),
      };
    case 'SET_OBS': {
      const observaciones = String(action.observaciones || '');
      return {
        ...state,
        items: state.items.map((i) =>
          i.lineId === action.lineId ? { ...i, observaciones } : i
        ),
      };
    }
    case 'SET_CHECKOUT':
      return { ...state, checkout: { ...state.checkout, ...action.data } };
    case 'CLEAR':
      return { items: [], checkout: emptyCheckout() };
    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(cartReducer, {
    items: [],
    checkout: emptyCheckout(),
  });

  const value = useMemo(() => {
    const totalItems = state.items.reduce((s, i) => s + i.cantidad, 0);
    const subtotal = state.items.reduce((s, i) => s + i.precio * i.cantidad, 0);
    return { state, dispatch, totalItems, subtotal };
  }, [state]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart debe usarse dentro de CartProvider');
  return ctx;
}
